import React, { useState, useEffect, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import '../styles/modal.css';
import Insights from '../components/Insights';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const getDayType = (date) => {
  const day = date.getDay();
  if (day === 0) return 'sunday';
  if (day === 6) return 'saturday';
  return 'weekday';
};

const CreateTaskModal = ({ isOpen, onClose, onCreateTask }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [schedule, setSchedule] = useState('daily');
  const [timeBlock, setTimeBlock] = useState('morning');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [weeklyTarget, setWeeklyTarget] = useState('');
  const [targetUnit, setTargetUnit] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    onCreateTask({
      title: title.trim(),
      description: description.trim(),
      category: category.trim(),
      schedule,
      timeBlock,
      startTime,
      endTime,
      weeklyTarget: weeklyTarget ? Number(weeklyTarget) : undefined,
      targetUnit: targetUnit.trim()
    });

    // Reset form
    setTitle('');
    setDescription('');
    setCategory('');
    setSchedule('daily');
    setTimeBlock('morning');
    setStartTime('');
    setEndTime('');
    setWeeklyTarget('');
    setTargetUnit('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 overflow-y-auto">
      <div className="bg-gray-800 p-6 rounded-xl w-full max-w-md border-2 border-gray-700 m-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl text-white mb-4">Create New Task</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-400 mb-1 text-sm">Task Title *</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" required />
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-400 mb-1 text-sm">Schedule</label>
              <select value={schedule} onChange={(e) => setSchedule(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600">
                <option value="daily">Daily</option>
                <option value="weekday">Weekday</option>
                <option value="saturday">Saturday</option>
                <option value="sunday">Sunday</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-400 mb-1 text-sm">Time Block</label>
              <select value={timeBlock} onChange={(e) => setTimeBlock(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600">
                <option value="morning">Morning</option>
                <option value="office">Office</option>
                <option value="afternoon">Afternoon</option>
                <option value="night">Night</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-400 mb-1 text-sm">Start Time</label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600" />
            </div>
            <div>
              <label className="block text-gray-400 mb-1 text-sm">End Time</label>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600" />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-gray-400 mb-1 text-sm">Category</label>
            <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600" placeholder="e.g. GS1, Optional" />
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-400 mb-1 text-sm">Weekly Target</label>
              <input type="number" value={weeklyTarget} onChange={(e) => setWeeklyTarget(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600" placeholder="e.g. 5" />
            </div>
            <div>
              <label className="block text-gray-400 mb-1 text-sm">Target Unit</label>
              <input type="text" value={targetUnit} onChange={(e) => setTargetUnit(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600" placeholder="e.g. answers" />
            </div>
          </div>
          <div className="mb-6">
            <label className="block text-gray-400 mb-1 text-sm">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600" rows="2" />
          </div>
          <div className="flex justify-end space-x-2">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Create Task</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const getCompletionColor = (pct) => {
  if (pct <= 0) return null;
  const hue = Math.round((pct / 100) * 130);
  return `hsl(${hue}, 75%, ${pct >= 100 ? 45 : 42}%)`;
};

const CalendarView = ({ completionByDate, onDateClick, selectedDate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const renderCalendar = () => {
    const firstDayOfWeek = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1;
    const paddingDays = Array(firstDayOfWeek).fill(null);
    const calendarCells = [...paddingDays, ...calendarDays];

    return (
      <div className="grid grid-cols-7 gap-1 text-center">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
          <div key={day} className="text-gray-400 text-xs">{day}</div>
        ))}
        {calendarCells.map((day, index) => {
          const dateISO = day ? format(day, 'yyyy-MM-dd') : '';
          const completion = day ? completionByDate[dateISO] : null;
          const bgColor = completion ? getCompletionColor(completion.pct) : null;
          const isSelected = day && dateISO === format(selectedDate, 'yyyy-MM-dd');

          return (
            <div
              key={index}
              className={`p-2 ${!day ? '' : 'border rounded cursor-pointer'} ${isSelected ? 'rounded-xl ring-2 ring-blue-500' : ''}`}
              style={bgColor ? { backgroundColor: bgColor, color: '#fff' } : !day ? {} : { backgroundColor: '#374151', color: '#d1d5db' }}
              onClick={() => day && onDateClick(day)}
            >
              {day ? day.getDate() : ''}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-gray-800 text-white p-4 rounded-xl shadow-md border-2 border-gray-700 mb-4">
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => setCurrentMonth(prevMonth => new Date(prevMonth.getFullYear(), prevMonth.getMonth() - 1, 1))}
          className="text-gray-400 hover:text-white"
        >
          ←
        </button>
        <h2 className="text-lg font-semibold">{format(currentMonth, 'MMMM yyyy')}</h2>
        <button
          onClick={() => setCurrentMonth(prevMonth => new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 1))}
          className="text-gray-400 hover:text-white"
        >
          →
        </button>
      </div>
      {renderCalendar()}
    </div>
  );
};

const DurationModal = ({ isOpen, onClose, onSubmit, durationInput, setDurationInput }) => {
  if (!isOpen) return null;
  const handleSubmit = () => {
    onSubmit(durationInput);
    setDurationInput('');
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2 className="modal-header">Enter duration in minutes:</h2>
        <input type="number" value={durationInput} onChange={(e) => setDurationInput(e.target.value)} className="modal-input" />
        <div className="modal-buttons">
          <button onClick={handleSubmit} className="modal-button">Submit</button>
          <button onClick={onClose} className="modal-button">Cancel</button>
        </div>
      </div>
    </div>
  );
};

const TodayClassesWidget = ({ selectedDate }) => {
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const dateISO = format(selectedDate, 'yyyy-MM-dd');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/class-schedules`);
        const all = await res.json();
        const filtered = all.filter(c => {
          const classDate = new Date(c.date).toISOString().split('T')[0];
          return classDate === dateISO;
        });
        setClasses(filtered);
      } catch (e) { console.error(e); }
    };
    fetchClasses();
  }, [selectedDate]);

  const toggleComplete = async (cls) => {
    try {
      const endpoint = cls.status === 'completed' ? 'uncomplete' : 'complete';
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/class-schedules/${cls._id}/${endpoint}`, { method: 'PATCH' });
      setClasses(prev => prev.map(c => c._id === cls._id ? { ...c, status: cls.status === 'completed' ? 'scheduled' : 'completed' } : c));
    } catch (e) { console.error(e); }
  };

  if (classes.length === 0) return (
    <div className="bg-gray-800 text-white p-4 rounded-xl shadow-md border-2 border-gray-700">
      <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
        <span className="text-blue-400">📚</span> Classes
      </h2>
      <div className="text-sm text-gray-400 text-center py-2">No classes for this day</div>
    </div>
  );

  return (
    <div className="bg-gray-800 text-white p-4 rounded-xl shadow-md border-2 border-gray-700">
      <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
        <span className="text-blue-400">📚</span> Today's Classes
      </h2>
      <div className="space-y-2">
        {classes.map(cls => (
          <div key={cls._id} className="flex items-center gap-2 p-2 rounded bg-gray-700">
            <input type="checkbox" checked={cls.status === 'completed'} onChange={() => toggleComplete(cls)} className="accent-green-500 w-4 h-4" />
            <span className={`text-sm ${cls.status === 'completed' ? 'line-through text-gray-500' : 'text-white'}`}>{cls.subject}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const BacklogWidget = () => {
  const [backlogs, setBacklogs] = useState([]);
  const [newSubject, setNewSubject] = useState('');
  const [newRemaining, setNewRemaining] = useState('');

  const fetchBacklogs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/backlogs`);
      const data = await response.json();
      setBacklogs(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchBacklogs();
  }, []);

  const handleUpdate = async (id, remaining) => {
    if (remaining < 0) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/backlogs/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ remaining })
      });
      fetchBacklogs();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/backlogs/${id}`, {
        method: 'DELETE'
      });
      fetchBacklogs();
    } catch (e) {
      console.error(e);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newSubject.trim() || !newRemaining) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/backlogs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: newSubject, remaining: Number(newRemaining) })
      });
      setNewSubject('');
      setNewRemaining('');
      fetchBacklogs();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="bg-gray-800 text-white p-4 rounded-xl shadow-md border-2 border-gray-700 mt-4">
      <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
        <span className="text-red-400">📚</span> Backlog Tracker
      </h2>
      <div className="space-y-3 mb-4">
        {backlogs.map(b => (
          <div key={b._id} className={`flex items-center justify-between p-2 rounded ${b.remaining > 3 ? 'bg-red-900/30 border border-red-800' : 'bg-gray-700'}`}>
            <span className="font-medium">{b.subject}</span>
            <div className="flex items-center gap-3">
              <span className={`font-bold ${b.remaining > 3 ? 'text-red-400' : 'text-white'}`}>{b.remaining} left</span>
              <button onClick={() => handleUpdate(b._id, b.remaining + 1)} className="w-8 h-8 rounded bg-gray-600 hover:bg-gray-500 flex items-center justify-center">+</button>
                <button onClick={() => handleUpdate(b._id, b.remaining - 1)} className="w-8 h-8 rounded bg-gray-600 hover:bg-gray-500 flex items-center justify-center">-</button>
              <button onClick={() => handleDelete(b._id)} className="w-8 h-8 rounded bg-red-900/50 hover:bg-red-800 flex items-center justify-center text-red-300">×</button>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={handleAdd} className="flex gap-2">
        <input type="text" value={newSubject} onChange={e => setNewSubject(e.target.value)} placeholder="Subject" className="flex-1 p-2 bg-gray-700 rounded border border-gray-600 text-sm" />
        <input type="number" value={newRemaining} onChange={e => setNewRemaining(e.target.value)} placeholder="Count" className="w-20 p-2 bg-gray-700 rounded border border-gray-600 text-sm" />
        <button type="submit" className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">+</button>
      </form>
    </div>
  );
};

const MetricConfigModal = ({ isOpen, onClose, configs, onSave }) => {
  const [edits, setEdits] = useState({});

  useEffect(() => {
    if (configs) {
      const initial = {};
      Object.entries(configs).forEach(([key, cfg]) => {
        initial[key] = { minTarget: cfg.minTarget, maxTarget: cfg.maxTarget };
      });
      setEdits(initial);
    }
  }, [configs]);

  if (!isOpen || !configs) return null;

  const handleSave = () => {
    onSave(edits);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-gray-800 p-6 rounded-xl w-full max-w-lg border-2 border-gray-700 m-4 max-h-[80vh] overflow-y-auto">
        <h2 className="text-xl text-white mb-4">Configure Weekly Targets</h2>
        <div className="space-y-4">
          {Object.entries(configs).map(([key, cfg]) => (
            <div key={key} className="flex items-center gap-3 bg-gray-700 p-3 rounded-lg">
              <span className="text-lg">{cfg.icon}</span>
              <span className="text-white text-sm font-medium flex-1">{cfg.label}</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={edits[key]?.minTarget ?? cfg.minTarget}
                  onChange={(e) => setEdits(prev => ({ ...prev, [key]: { ...prev[key], minTarget: Number(e.target.value) } }))}
                  className="w-16 p-1 bg-gray-600 text-white rounded border border-gray-500 text-center text-sm"
                />
                <span className="text-gray-400 text-sm">to</span>
                <input
                  type="number"
                  value={edits[key]?.maxTarget ?? cfg.maxTarget}
                  onChange={(e) => setEdits(prev => ({ ...prev, [key]: { ...prev[key], maxTarget: Number(e.target.value) } }))}
                  className="w-16 p-1 bg-gray-600 text-white rounded border border-gray-500 text-center text-sm"
                />
                <span className="text-gray-500 text-xs">{cfg.unit}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end space-x-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600">Cancel</button>
          <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Save Targets</button>
        </div>
      </div>
    </div>
  );
};

const MetricsTab = () => {
  const [metrics, setMetrics] = useState(null);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);

  const fetchMetrics = async (offset) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/metrics/weekly?weekOffset=${offset}`);
      const data = await response.json();
      setMetrics(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchMetrics(weekOffset);
  }, [weekOffset]);

  const handlePrevWeek = () => setWeekOffset(prev => prev - 1);
  const handleNextWeek = () => setWeekOffset(prev => prev + 1);
  const handleCurrentWeek = () => setWeekOffset(0);

  const handleConfigSave = async (edits) => {
    try {
      await Promise.all(
        Object.entries(edits).map(([key, values]) =>
          fetch(`${process.env.REACT_APP_BACKEND_URL}/metric-configs/${key}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(values)
          })
        )
      );
      fetchMetrics(weekOffset);
    } catch (e) {
      console.error(e);
    }
  };

  if (!metrics) return <div className="text-white text-center py-8">Loading metrics...</div>;

  const { configs } = metrics;

  const metricKeys = ['answersWritten', 'pyqsDone', 'backlogLectures', 'revisionSessions', 'newspaperDays', 'totalStudyHours'];
  const cards = metricKeys.map(key => {
    const cfg = configs?.[key] || {};
    const value = key === 'totalStudyHours'
      ? (metrics.totalStudyMinutes ? Math.round(metrics.totalStudyMinutes / 60) : 0)
      : (metrics[key] || 0);
    return {
      key,
      title: cfg.label || key,
      value,
      minTarget: cfg.minTarget || 0,
      maxTarget: cfg.maxTarget || 0,
      targetLabel: cfg.minTarget === cfg.maxTarget ? `${cfg.maxTarget}` : `${cfg.minTarget}-${cfg.maxTarget}`,
      icon: cfg.icon || '📊',
    };
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <button onClick={handlePrevWeek} className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm">←</button>
          <button onClick={handleCurrentWeek} className={`px-3 py-2 rounded-lg text-sm transition-colors ${weekOffset === 0 ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>This Week</button>
          <button onClick={handleNextWeek} className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm">→</button>
        </div>
        {metrics?.weekStart && metrics?.weekEnd && (
          <span className="text-sm text-gray-400">
            {new Date(metrics.weekStart).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - {new Date(metrics.weekEnd).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        )}
        <button onClick={() => setIsConfigOpen(true)} className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm flex items-center gap-2">
          ⚙️ Configure Targets
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => {
          const progress = c.maxTarget > 0 ? Math.min(100, (c.value / c.maxTarget) * 100) : 0;
          const colorClass = progress >= 80 ? 'bg-green-500' : progress >= 40 ? 'bg-yellow-500' : 'bg-red-500';
          return (
            <div key={c.key} className="bg-gray-800 p-4 rounded-xl border border-gray-700 shadow-md">
              <div className="flex items-center gap-2 mb-2 text-gray-400">
                <span>{c.icon}</span>
                <h3 className="text-sm font-medium">{c.title}</h3>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-2xl font-bold text-white">{c.value}</span>
                <span className="text-sm text-gray-500">/ {c.targetLabel}</span>
              </div>
              <div className="w-full bg-gray-700 h-2 rounded-full overflow-hidden">
                <div className={`h-full ${colorClass}`} style={{ width: `${progress}%` }}></div>
              </div>
            </div>
          );
        })}
      </div>
      <MetricConfigModal isOpen={isConfigOpen} onClose={() => setIsConfigOpen(false)} configs={configs} onSave={handleConfigSave} />
    </div>
  );
};

const ClassesTab = () => {
  const [weekData, setWeekData] = useState({ classes: [], weekStart: null, weekEnd: null, total: 0, completed: 0 });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  const [date, setDate] = useState('');
  const [subject, setSubject] = useState('');

  const fetchClasses = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/class-schedules/week`);
      const data = await res.json();
      setWeekData(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { fetchClasses(); }, []);

  const handleComplete = async (id, isCompleted) => {
    try {
      const endpoint = isCompleted ? 'uncomplete' : 'complete';
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/class-schedules/${id}/${endpoint}`, { method: 'PATCH' });
      fetchClasses();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this class?')) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/class-schedules/${id}`, { method: 'DELETE' });
      fetchClasses();
    } catch (e) { console.error(e); }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!date || !subject) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/class-schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, subject })
      });
      setSubject('');
      fetchClasses();
    } catch (e) { console.error(e); }
  };

  const handleLoadNewWeek = async () => {
    if (!window.confirm('This will process any missed classes into backlogs and replace the current schedule. Continue?')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/class-schedules/new-week`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ classes: [] })
      });
      const data = await res.json();
      alert(`Done! ${data.processed || 0} missed classes added to backlogs.`);
      fetchClasses();
      setIsAddModalOpen(true);
    } catch (e) { console.error(e); }
  };

  const todayISO = format(new Date(), 'yyyy-MM-dd');

  const groupedByDay = weekData.classes.reduce((acc, cls) => {
    const d = (cls.date ? new Date(cls.date) : new Date()).toISOString().split('T')[0];
    if (!acc[d]) acc[d] = [];
    acc[d].push(cls);
    return acc;
  }, {});

  const renderClassCard = (cls) => (
    <div key={cls._id} className="bg-gray-800 p-3 rounded-xl border border-gray-700 mb-2 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <input type="checkbox" checked={cls.status === 'completed'} onChange={() => handleComplete(cls._id, cls.status === 'completed')} className="accent-green-500 w-4 h-4" />
        <span className={`font-medium ${cls.status === 'completed' ? 'line-through text-gray-500' : 'text-white'}`}>
          {cls.subject}
        </span>
      </div>
      <button onClick={() => handleDelete(cls._id)} className="text-red-500 hover:text-red-400 p-1 text-sm transition-opacity">🗑️</button>
    </div>
  );

  const hasSchedule = weekData.weekStart && weekData.weekEnd;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="bg-gray-800 p-5 rounded-xl border-2 border-gray-700 shadow-md flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-white mb-2">Class Schedule</h2>
          {hasSchedule ? (
            <div className="text-sm text-gray-400">
              <p className="mb-2">
                {new Date(weekData.weekStart).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - {new Date(weekData.weekEnd).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
              <div className="flex items-center gap-2">
                <div className="w-48 bg-gray-700 h-2 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500" style={{ width: `${weekData.total > 0 ? (weekData.completed / weekData.total) * 100 : 0}%` }}></div>
                </div>
                <span>{weekData.completed}/{weekData.total}</span>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-400">No schedule loaded</div>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setIsAddModalOpen(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">+ Add Class</button>
          <button onClick={handleLoadNewWeek} className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium">Load New Week</button>
        </div>
      </div>

      {weekData.classes.length === 0 ? (
        <div className="bg-gray-800 p-6 rounded-xl border-2 border-gray-700 text-center text-gray-400">No classes this week 📭</div>
      ) : (
        Object.keys(groupedByDay).sort().map(dateStr => {
          const isToday = dateStr === todayISO;
          return (
            <div key={dateStr} className={isToday ? 'ring-2 ring-blue-500/50 rounded-xl p-1' : ''}>
              <h3 className={`text-lg font-semibold mb-3 ${isToday ? 'text-blue-400' : 'text-white'}`}>
                {isToday ? '📅' : ''} {new Date(dateStr).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                {isToday && <span className="text-sm font-normal ml-2 text-blue-300">Today</span>}
              </h3>
              {groupedByDay[dateStr].map(cls => renderClassCard(cls))}
            </div>
          );
        })
      )}

      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-gray-800 p-6 rounded-xl w-full max-w-md border-2 border-gray-700 m-4">
            <h2 className="text-xl text-white mb-4">Add Class</h2>
            <form onSubmit={handleAddSubmit}>
              <div className="mb-4">
                <label className="block text-gray-400 mb-1 text-sm">Date *</label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div className="mb-6">
                <label className="block text-gray-400 mb-1 text-sm">Subject *</label>
                <input type="text" value={subject} onChange={e => setSubject(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. Geography Class 10" required />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Form stays open after adding</span>
                <div className="flex space-x-2">
                  <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600">Done</button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Add</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const QuestionBankTab = () => {
  const [questions, setQuestions] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [newQuestionText, setNewQuestionText] = useState('');
  const [newMaxMarks, setNewMaxMarks] = useState(10);
  const [newHidden, setNewHidden] = useState(true);
  const [showQuestions, setShowQuestions] = useState(false);

  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [pickedQuestion, setPickedQuestion] = useState(null);
  const [marksInput, setMarksInput] = useState('');
  const [randomMessage, setRandomMessage] = useState('');

  const fetchQuestions = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/question-bank`);
      const data = await res.json();
      setQuestions(data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchQuestions(); }, []);

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!newSubject || !newTopic || !newQuestionText) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/question-bank`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: newSubject, topic: newTopic, questionText: newQuestionText, maxMarks: Number(newMaxMarks), hidden: newHidden })
      });
      setNewSubject('');
      setNewTopic('');
      setNewQuestionText('');
      setNewMaxMarks(10);
      setNewHidden(true);
      setIsAddModalOpen(false);
      fetchQuestions();
    } catch (e) { console.error(e); }
  };

  const handlePickRandom = async () => {
    try {
      const query = new URLSearchParams();
      if (selectedSubject) query.append('subject', selectedSubject);
      if (selectedTopic) query.append('topic', selectedTopic);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/question-bank/random?${query.toString()}`);
      if (res.status === 404) {
        setPickedQuestion(null);
        setRandomMessage('No pending questions for this subject/topic');
      } else if (res.ok) {
        const data = await res.json();
        setPickedQuestion(data);
        setRandomMessage('');
        setMarksInput('');
      }
    } catch (e) { console.error(e); }
  };

  const handleSubmitMarks = async (e) => {
    e.preventDefault();
    if (!pickedQuestion || marksInput === '') return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/question-bank/${pickedQuestion._id}/submit-marks`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ marksObtained: Number(marksInput) })
      });
      setPickedQuestion(null);
      setRandomMessage(`Submitted! ${marksInput}/${pickedQuestion.maxMarks}`);
      fetchQuestions();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/question-bank/${id}`, { method: 'DELETE' });
      if (pickedQuestion && pickedQuestion._id === id) setPickedQuestion(null);
      fetchQuestions();
    } catch (e) { console.error(e); }
  };

  const subjects = [...new Set(questions.map(q => q.subject))];
  const topics = [...new Set(questions.filter(q => q.subject === selectedSubject).map(q => q.topic))];

  useEffect(() => {
    setSelectedTopic('');
  }, [selectedSubject]);

  const filteredQuestions = questions.filter(q => {
    if (selectedSubject && q.subject !== selectedSubject) return false;
    if (selectedTopic && q.topic !== selectedTopic) return false;
    return true;
  });

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="bg-gray-800 p-5 rounded-xl border-2 border-gray-700 shadow-md flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-white mb-2">Question Bank</h2>
          <div className="text-sm text-gray-400">Practice and evaluate questions</div>
        </div>
        <button onClick={() => setIsAddModalOpen(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">+ Add Question</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-800 p-5 rounded-xl border border-gray-700 shadow-md flex flex-col">
          <h3 className="text-lg font-semibold text-white mb-4">Practice</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-400 mb-1 text-sm">Subject</label>
              <select value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">All Subjects</option>
                {subjects.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-gray-400 mb-1 text-sm">Topic</label>
              <select value={selectedTopic} onChange={(e) => setSelectedTopic(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" disabled={!selectedSubject}>
                <option value="">All Topics</option>
                {topics.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <button onClick={handlePickRandom} className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium mb-4">Pick Random Question</button>

          {randomMessage && !pickedQuestion && (
            <div className="p-3 bg-gray-700 rounded-lg text-gray-300 text-center text-sm">{randomMessage}</div>
          )}

          {pickedQuestion && (
            <div className="mt-2 p-4 bg-gray-700 rounded-xl border border-gray-600">
              <div className="flex gap-2 mb-2 text-xs">
                <span className="bg-gray-800 px-2 py-1 rounded text-gray-300">{pickedQuestion.subject}</span>
                <span className="bg-gray-800 px-2 py-1 rounded text-gray-300">{pickedQuestion.topic}</span>
                <span className="bg-blue-900/50 text-blue-300 px-2 py-1 rounded font-medium">{pickedQuestion.maxMarks} Marks</span>
              </div>
              <p className="text-white mb-4 whitespace-pre-wrap">{pickedQuestion.questionText}</p>
              <form onSubmit={handleSubmitMarks} className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="block text-gray-400 mb-1 text-xs">Marks Obtained</label>
                  <input type="number" min="0" max={pickedQuestion.maxMarks} step="0.5" value={marksInput} onChange={(e) => setMarksInput(e.target.value)} className="w-full p-2 bg-gray-800 text-white rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors">Submit</button>
              </form>
            </div>
          )}
        </div>

        <div className="bg-gray-800 p-5 rounded-xl border border-gray-700 shadow-md flex flex-col max-h-[600px]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-white">Questions ({filteredQuestions.length})</h3>
            <button onClick={() => setShowQuestions(prev => !prev)} className="text-sm px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors">
              {showQuestions ? 'Hide' : 'Show'}
            </button>
          </div>
          {showQuestions ? (
            <div className="overflow-y-auto pr-2 space-y-2 flex-1">
              {filteredQuestions.length === 0 ? (
                <div className="text-center text-gray-500 py-8">No questions found.</div>
              ) : (
                filteredQuestions.map(q => (
                  <div key={q._id} className="bg-gray-700 p-3 rounded-lg flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm line-clamp-2 mb-1" title={q.questionText}>{q.questionText}</p>
                      <div className="flex gap-2 items-center text-xs">
                        <span className="text-gray-400">{q.subject} · {q.topic}</span>
                        {q.hidden && (
                          <span className="bg-purple-900/50 text-purple-300 px-1.5 py-0.5 rounded">Hidden</span>
                        )}
                        {q.status === 'done' ? (
                          <span className="text-green-400 font-medium">{q.marksObtained}/{q.maxMarks}</span>
                        ) : (
                          <span className="text-yellow-500">Pending</span>
                        )}
                      </div>
                    </div>
                    <button onClick={() => handleDelete(q._id)} className="text-red-500 hover:text-red-400 text-sm p-1">🗑️</button>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8 text-sm">Questions are hidden to keep practice fresh. Click "Show" to reveal, or use Pick Random.</div>
          )}
        </div>
      </div>

      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-gray-800 p-6 rounded-xl w-full max-w-md border-2 border-gray-700 m-4">
            <h2 className="text-xl text-white mb-4">Add Question</h2>
            <form onSubmit={handleAddSubmit}>
              <div className="mb-4">
                <label className="block text-gray-400 mb-1 text-sm">Subject *</label>
                <input type="text" value={newSubject} onChange={e => setNewSubject(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div className="mb-4">
                <label className="block text-gray-400 mb-1 text-sm">Topic *</label>
                <input type="text" value={newTopic} onChange={e => setNewTopic(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div className="mb-4">
                <label className="block text-gray-400 mb-1 text-sm">Question Text *</label>
                <textarea value={newQuestionText} onChange={e => setNewQuestionText(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" rows="3" required />
              </div>
              <div className="mb-6">
                <label className="block text-gray-400 mb-1 text-sm">Max Marks *</label>
                <select value={newMaxMarks} onChange={e => setNewMaxMarks(e.target.value)} className="w-full p-2 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                </select>
              </div>
              <label className="flex items-center gap-2 mb-6 cursor-pointer">
                <input type="checkbox" checked={newHidden} onChange={e => setNewHidden(e.target.checked)} className="accent-blue-500 w-4 h-4" />
                <span className="text-gray-300 text-sm">Hidden (include in random practice pool)</span>
              </label>
              <div className="flex justify-end space-x-2">
                <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const HabitsDashboard = () => {
  const [allTasks, setAllTasks] = useState([]);
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [completedTaskIds, setCompletedTaskIds] = useState([]);
  const [completionByDate, setCompletionByDate] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dayTypeOverride, setDayTypeOverride] = useState(null);
  
  const [taskDurations, setTaskDurations] = useState({});
  const [taskStatusMap, setTaskStatusMap] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [isEditingDuration, setIsEditingDuration] = useState(false);
  const [durationInput, setDurationInput] = useState('');
  const [activeTab, setActiveTab] = useState('planner');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const currentDayType = dayTypeOverride || getDayType(selectedDate);

  const fetchTaskStatuses = async () => {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/task-statuses`);
    return await response.json();
  };

  const createTask = async (taskData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(taskData)
      });
      if (response.ok) {
        fetchTasksForDateAndType(selectedDate, currentDayType);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const archiveTask = async (taskId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/tasks/${taskId}/archive`, { method: 'PATCH' });
      if (response.ok) {
        setAllTasks(prev => prev.filter(t => t._id !== taskId));
        setCompletedTaskIds(prev => prev.filter(id => id !== taskId));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete permanently?')) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/tasks/${taskId}`, { method: 'DELETE' });
      if (response.ok) {
        setAllTasks(prev => prev.filter(t => t._id !== taskId));
        setArchivedTasks(prev => prev.filter(t => t._id !== taskId));
        setCompletedTaskIds(prev => prev.filter(id => id !== taskId));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchArchivedTasks = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/tasks/archived`);
      const data = await response.json();
      setArchivedTasks(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const unarchiveTask = async (taskId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/tasks/${taskId}/unarchive`, { method: 'PATCH' });
      if (response.ok) {
        fetchArchivedTasks();
        fetchTasksForDateAndType(selectedDate, currentDayType);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const toggleTask = async (task) => {
    const selectedDateISO = format(selectedDate, 'yyyy-MM-dd');
    if (!completedTaskIds.includes(task._id)) {
      setCurrentTask(task);
      setDurationInput('');
      setIsEditingDuration(false);
      setIsModalOpen(true);
    } else {
      try {
        const statuses = await fetchTaskStatuses();
        const toDelete = statuses.find(s => s.task === task._id && s.createdAt.startsWith(selectedDateISO));
        if (toDelete) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/task-statuses/${toDelete._id}`, { method: 'DELETE' });
          setCompletedTaskIds(prev => prev.filter(id => id !== task._id));
          setTaskDurations(prev => {
            const next = { ...prev };
            delete next[`${task._id}_${selectedDateISO}`];
            return next;
          });
          const remaining = statuses.filter(s => s.createdAt.startsWith(selectedDateISO) && s.task !== task._id);
          if (remaining.length === 0) {
            setCompletionByDate(prev => {
              const next = { ...prev };
              delete next[selectedDateISO];
              return next;
            });
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleModalSubmit = async (duration) => {
    if (!duration) return;
    const selectedDateISO = format(selectedDate, 'yyyy-MM-dd');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/task-statuses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task: currentTask._id, date: selectedDateISO, duration: parseInt(duration, 10) })
      });
      if (response.ok) {
        const created = await response.json();
        setCompletedTaskIds(prev => [...prev, currentTask._id]);
        setTaskDurations(prev => ({ ...prev, [`${currentTask._id}_${selectedDateISO}`]: duration }));
        setTaskStatusMap(prev => ({ ...prev, [currentTask._id]: created._id }));
        setCompletionByDate(prev => {
          const existing = prev[selectedDateISO] || { completed: 0, total: allTasks.length || 1, pct: 0 };
          const newCompleted = existing.completed + 1;
          return { ...prev, [selectedDateISO]: { completed: newCompleted, total: existing.total, pct: Math.min(100, Math.round((newCompleted / existing.total) * 100)) } };
        });
        setIsModalOpen(false);
        setCurrentTask(null);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const editDuration = (task) => {
    const selectedDateISO = format(selectedDate, 'yyyy-MM-dd');
    setCurrentTask(task);
    setDurationInput(String(taskDurations[`${task._id}_${selectedDateISO}`] || ''));
    setIsEditingDuration(true);
    setIsModalOpen(true);
  };

  const handleEditSubmit = async (duration) => {
    if (!duration || !currentTask) return;
    const selectedDateISO = format(selectedDate, 'yyyy-MM-dd');
    const statusId = taskStatusMap[currentTask._id];
    if (!statusId) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/task-statuses/${statusId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ duration: parseInt(duration, 10) })
      });
      if (response.ok) {
        setTaskDurations(prev => ({ ...prev, [`${currentTask._id}_${selectedDateISO}`]: parseInt(duration, 10) }));
        setIsModalOpen(false);
        setCurrentTask(null);
        setIsEditingDuration(false);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchTasksForDateAndType = useCallback(async (date, type) => {
    try {
      // First try to fetch specific schedule
      let url = `${process.env.REACT_APP_BACKEND_URL}/tasks?schedule=${type}`;
      const res = await fetch(url);
      const data = await res.json();
      
      const statuses = await fetchTaskStatuses();
      const dateISO = format(date, 'yyyy-MM-dd');
      
      const completedIds = statuses
        .filter(s => s.createdAt.startsWith(dateISO))
        .map(s => s.task);
        
      setAllTasks(data);
      setCompletedTaskIds(completedIds);
      
      const statusMap = {};
      statuses.filter(s => s.createdAt.startsWith(dateISO)).forEach(s => {
        statusMap[s.task] = s._id;
      });
      setTaskStatusMap(statusMap);
      
      // Update durations map
      const durations = statuses.reduce((acc, status) => {
        acc[`${status.task}_${status.createdAt.split('T')[0]}`] = status.duration || 0;
        return acc;
      }, {});
      setTaskDurations(durations);

      const totalTasks = data.length || 1;
      const byDate = {};
      statuses.forEach(s => {
        const d = s.createdAt.split('T')[0];
        if (!byDate[d]) byDate[d] = new Set();
        byDate[d].add(s.task);
      });
      const completionMap = {};
      Object.entries(byDate).forEach(([d, taskIds]) => {
        const count = taskIds.size;
        completionMap[d] = { completed: count, total: totalTasks, pct: Math.min(100, Math.round((count / totalTasks) * 100)) };
      });
      setCompletionByDate(completionMap);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchTasksForDateAndType(selectedDate, currentDayType).finally(() => setIsLoading(false));
    fetchArchivedTasks();
  }, [selectedDate, currentDayType, fetchTasksForDateAndType, fetchArchivedTasks]);

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setDayTypeOverride(null); // Reset override on new date selection
  };

  const renderTimeBlock = (blockTitle, blockIcon, tasks) => {
    if (!tasks || tasks.length === 0) return null;
    
    // Sort tasks by startTime
    const parseTime = (t) => {
      if (!t) return -1;
      const [h, m] = t.split(':').map(Number);
      return h * 60 + (m || 0);
    };

    const sortedTasks = [...tasks].sort((a, b) => {
      const aTime = parseTime(a.startTime);
      const bTime = parseTime(b.startTime);
      if (aTime === -1) return 1;
      if (bTime === -1) return -1;
      if (aTime < 720 && bTime >= 720) return 1;
      if (aTime >= 720 && bTime < 720) return -1;
      return aTime - bTime;
    });

    return (
      <div className="bg-gray-800 rounded-xl shadow-md border-2 border-gray-700 mb-4 overflow-hidden">
        <div className="bg-gray-750 px-4 py-2 border-b border-gray-700 flex items-center gap-2">
          <span className="text-xl">{blockIcon}</span>
          <h3 className="font-semibold text-white">{blockTitle}</h3>
        </div>
        <div className="p-2 space-y-1">
          {sortedTasks.map((task) => (
            <label key={task._id} className="flex items-center justify-between gap-2 text-sm py-2 px-2 cursor-pointer hover:bg-gray-700 rounded transition-colors">
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={completedTaskIds.includes(task._id)} onChange={() => toggleTask(task)} className="accent-green-500 w-4 h-4" />
                <div className="flex flex-col">
                  <span className={completedTaskIds.includes(task._id) ? "line-through text-gray-500" : "text-white font-medium"}>
                    {task.title}
                  </span>
                  {(task.startTime || task.endTime) && (
                    <span className="text-xs text-gray-400">
                      {task.startTime || '?'} - {task.endTime || '?'}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {completedTaskIds.includes(task._id) && (
                  <button
                    onClick={(e) => { e.preventDefault(); editDuration(task); }}
                    className="text-gray-400 text-xs bg-gray-900 px-2 py-1 rounded hover:bg-gray-600 hover:text-white transition-colors"
                    title="Edit duration"
                  >
                    {taskDurations[`${task._id}_${format(selectedDate, 'yyyy-MM-dd')}`]}m ✏️
                  </button>
                )}
                <div className="flex gap-1 opacity-0 hover:opacity-100 focus-within:opacity-100 transition-opacity">
                  <button onClick={(e) => { e.preventDefault(); archiveTask(task._id); }} className="text-yellow-500 hover:text-yellow-400 p-1" title="Archive">📦</button>
                  <button onClick={(e) => { e.preventDefault(); deleteTask(task._id); }} className="text-red-500 hover:text-red-400 p-1" title="Delete">🗑️</button>
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const tasksByBlock = {
    morning: allTasks.filter(t => t.timeBlock === 'morning'),
    office: allTasks.filter(t => t.timeBlock === 'office'),
    afternoon: allTasks.filter(t => t.timeBlock === 'afternoon'),
    night: allTasks.filter(t => t.timeBlock === 'night'),
    unassigned: allTasks.filter(t => !['morning', 'office', 'afternoon', 'night'].includes(t.timeBlock))
  };

  if (isLoading && allTasks.length === 0) {
    return <div className="p-4 bg-gray-900 min-h-screen flex items-center justify-center"><div className="text-white">Loading...</div></div>;
  }

  return (
    <div className="p-4 bg-gray-900 min-h-screen font-sans">
      <div className="max-w-6xl mx-auto">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          <button className={`px-6 py-2 font-medium rounded-full transition-colors ${activeTab === 'planner' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} onClick={() => setActiveTab('planner')}>Planner</button>
          <button className={`px-6 py-2 font-medium rounded-full transition-colors ${activeTab === 'classes' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} onClick={() => setActiveTab('classes')}>Classes</button>
          <button className={`px-6 py-2 font-medium rounded-full transition-colors ${activeTab === 'insights' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} onClick={() => setActiveTab('insights')}>Insights</button>
          <button className={`px-6 py-2 font-medium rounded-full transition-colors ${activeTab === 'metrics' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} onClick={() => setActiveTab('metrics')}>Metrics</button>
          <button className={`px-6 py-2 font-medium rounded-full transition-colors ${activeTab === 'questionBank' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} onClick={() => setActiveTab('questionBank')}>Question Bank</button>
          <button className={`px-6 py-2 font-medium rounded-full transition-colors ${activeTab === 'archived' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} onClick={() => setActiveTab('archived')}>Archived ({archivedTasks.length})</button>
        </div>

        {activeTab === 'planner' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Tasks */}
            <div className="lg:col-span-2 space-y-4">
              {/* Day Type Toggle and Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gray-800 p-4 rounded-xl border border-gray-700 shadow-sm">
                <div>
                  <h1 className="text-2xl font-bold text-white mb-1">{format(selectedDate, 'EEEE, MMMM d')}</h1>
                  <div className="flex gap-2">
                    {['weekday', 'saturday', 'sunday'].map(type => (
                      <button 
                        key={type} 
                        onClick={() => {
                          setDayTypeOverride(type);
                          const today = new Date();
                          const todayDay = today.getDay();
                          const targetDay = type === 'saturday' ? 6 : type === 'sunday' ? 0 : (todayDay === 0 || todayDay === 6 ? 5 : todayDay);
                          let diff = todayDay - targetDay;
                          if (diff < 0) diff += 7;
                          const targetDate = new Date(today);
                          targetDate.setDate(today.getDate() - diff);
                          setSelectedDate(targetDate);
                        }}
                        className={`text-xs px-3 py-1 rounded-full capitalize transition-colors ${currentDayType === type ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <button onClick={() => setIsCreateModalOpen(true)} className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium flex items-center gap-2">
                  <span>+</span> New Task
                </button>
              </div>

              <TodayClassesWidget selectedDate={selectedDate} />

              {/* Task Blocks */}
              {allTasks.length === 0 ? (
                <div className="bg-gray-800 rounded-xl p-8 text-center border-2 border-dashed border-gray-700">
                  <div className="text-5xl mb-4">📝</div>
                  <h3 className="text-xl text-white font-medium mb-2">No tasks scheduled</h3>
                  <p className="text-gray-400 mb-6">Create a task for {currentDayType}s to get started.</p>
                  <button onClick={() => setIsCreateModalOpen(true)} className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors">Create First Task</button>
                </div>
              ) : (
                <>
                  {renderTimeBlock('Morning', '☀️', tasksByBlock.morning)}
                  {renderTimeBlock('Office', '🏢', tasksByBlock.office)}
                  {renderTimeBlock('Afternoon', '🌤️', tasksByBlock.afternoon)}
                  {renderTimeBlock('Night', '🌙', tasksByBlock.night)}
                  {renderTimeBlock('Unassigned', '📌', tasksByBlock.unassigned)}
                </>
              )}
            </div>

            {/* Right Column - Calendar & Widgets */}
            <div className="space-y-4">
              <CalendarView completionByDate={completionByDate} onDateClick={handleDateClick} selectedDate={selectedDate} />
              <BacklogWidget />
            </div>
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
            <Insights taskDurations={taskDurations} />
          </div>
        )}

        {activeTab === 'classes' && (
          <ClassesTab />
        )}

        {activeTab === 'metrics' && (
          <MetricsTab />
        )}

        {activeTab === 'questionBank' && (
          <QuestionBankTab />
        )}

        {activeTab === 'archived' && (
          <div className="bg-gray-800 text-white p-6 rounded-xl shadow-md border border-gray-700 max-w-3xl mx-auto">
            <h2 className="text-xl font-bold mb-6">Archived Tasks</h2>
            {archivedTasks.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-5xl mb-4 block">📦</span>
                <p className="text-xl text-gray-300">No archived tasks</p>
              </div>
            ) : (
              <div className="space-y-3">
                {archivedTasks.map((task) => (
                  <div key={task._id} className="flex items-center justify-between p-4 bg-gray-900 rounded-lg border border-gray-700">
                    <div>
                      <h3 className="font-medium text-lg">{task.title}</h3>
                      {task.description && <p className="text-gray-400 text-sm mt-1">{task.description}</p>}
                      <div className="flex gap-3 mt-2 text-xs text-gray-500">
                        <span className="capitalize px-2 py-0.5 bg-gray-800 rounded">{task.schedule || 'daily'}</span>
                        {task.timeBlock && <span className="capitalize px-2 py-0.5 bg-gray-800 rounded">{task.timeBlock}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button onClick={() => unarchiveTask(task._id)} className="text-sm px-3 py-1.5 bg-gray-800 text-blue-400 hover:bg-gray-700 hover:text-blue-300 rounded border border-gray-700 transition-colors">Restore</button>
                      <button onClick={() => deleteTask(task._id)} className="text-sm px-3 py-1.5 bg-gray-800 text-red-400 hover:bg-gray-700 hover:text-red-300 rounded border border-gray-700 transition-colors">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
      
      <CreateTaskModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onCreateTask={createTask} />
      <DurationModal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setIsEditingDuration(false); setDurationInput(''); }} onSubmit={isEditingDuration ? handleEditSubmit : handleModalSubmit} durationInput={durationInput} setDurationInput={setDurationInput} />
    </div>
  );
};

export default HabitsDashboard;
